import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslationObject } from '@ngx-translate/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TRANSLATION_LEAF } from '../../utils/translation-display-text.util';
import {
  ResourceApiResponse,
  ResourceDto,
  ResourceQueryParams,
  SaveResourceRangeRequest,
  SaveResourceRequest,
  resourceLabelName,
} from '../../interfaces/resources.interfaces';

/**
 * Localization Resources — API (`/lms/api/Resources`).
 * Admin CRUD + runtime dictionary for ngx-translate (JSON + API merge).
 */
@Injectable({ providedIn: 'root' })
export class ResourcesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseUrl}/lms/api/Resources`;

  getResources(params: ResourceQueryParams = {}): Observable<ResourceDto[]> {
    return this.http
      .get<ResourceApiResponse<ResourceDto[]> | ResourceDto[]>(this.baseUrl, {
        params: this.toHttpParams(params),
      })
      .pipe(map((res) => this.unwrapList(res).map((item) => this.normalize(item))));
  }

  /**
   * Loads resources for the selected locale and maps them to an ngx-translate dictionary.
   * Sends Accept-Language explicitly so it is not stale during language switches.
   */
  getTranslationDictionary(language: string): Observable<TranslationObject> {
    const headers = new HttpHeaders({
      'Accept-Language': language || 'en',
    });

    return this.http
      .get<ResourceApiResponse<ResourceDto[]> | ResourceDto[]>(this.baseUrl, { headers })
      .pipe(
        map((res) =>
          this.toTranslationDictionary(
            this.unwrapList(res).map((item) => this.normalize(item)),
            language,
          ),
        ),
      );
  }

  getResource(culture: string, labelName: string): Observable<ResourceDto | null> {
    return this.http
      .get<ResourceApiResponse<ResourceDto> | ResourceDto>(this.resourceUrl(culture, labelName))
      .pipe(map((res) => this.normalizeNullable(this.unwrapOne(res))));
  }

  createResource(payload: SaveResourceRequest): Observable<ResourceDto | null> {
    return this.http
      .post<ResourceApiResponse<ResourceDto> | ResourceDto>(this.baseUrl, payload)
      .pipe(map((res) => this.normalizeNullable(this.unwrapOne(res))));
  }

  /** One POST to `/Resources/bulk` with multiple culture rows (Add Range). */
  createResources(payload: SaveResourceRangeRequest): Observable<ResourceDto[]> {
    return this.http
      .post<ResourceApiResponse<ResourceDto[]> | ResourceDto[]>(`${this.baseUrl}/bulk`, payload)
      .pipe(map((res) => this.unwrapList(res).map((item) => this.normalize(item))));
  }

  updateResource(payload: SaveResourceRequest): Observable<ResourceDto | null> {
    return this.http
      .put<ResourceApiResponse<ResourceDto> | ResourceDto>(this.baseUrl, payload)
      .pipe(map((res) => this.normalizeNullable(this.unwrapOne(res))));
  }

  deleteResource(culture: string, labelName: string): Observable<unknown> {
    return this.http.delete(this.resourceUrl(culture, labelName));
  }

  private toHttpParams(params: ResourceQueryParams): HttpParams {
    let httpParams = new HttpParams();
    const search = params.search?.trim();
    if (search) {
      httpParams = httpParams.set('search', search);
    }
    return httpParams;
  }

  private resourceUrl(culture: string, labelName: string): string {
    return `${this.baseUrl}/${encodeURIComponent(culture)}/${encodeURIComponent(labelName)}`;
  }

  private unwrapList(res: ResourceApiResponse<ResourceDto[]> | ResourceDto[]): ResourceDto[] {
    if (Array.isArray(res)) return res;
    const data = res?.data;
    return Array.isArray(data) ? data : [];
  }

  private unwrapOne(
    res: ResourceApiResponse<ResourceDto> | ResourceDto | null | undefined,
  ): ResourceDto | null {
    if (!res) return null;
    const enveloped = res as ResourceApiResponse<ResourceDto>;
    if (enveloped.data && typeof enveloped.data === 'object' && 'culture' in enveloped.data) {
      return enveloped.data;
    }
    const direct = res as ResourceDto;
    if (direct.culture != null && resourceLabelName(direct)) {
      return direct;
    }
    return null;
  }

  private normalizeNullable(item: ResourceDto | null): ResourceDto | null {
    return item ? this.normalize(item) : null;
  }

  /** Maps legacy `name` responses onto `labelName`. */
  private normalize(item: ResourceDto): ResourceDto {
    const labelName = resourceLabelName(item);
    return {
      culture: String(item.culture ?? '').trim(),
      labelName,
      value: item.value == null ? '' : String(item.value),
      description: String(item.description ?? '').trim(),
      name: labelName,
    };
  }

  /**
   * Converts flat API rows (`labelName` / `value` per culture) into a nested dictionary
   * so dotted keys override matching nested keys from static JSON files.
   *
   * If a title key (e.g. `fieldCatalogue`) is already a string and a child
   * (`fieldCatalogue.clearOptionsTitle`) arrives, keep the string on `_` so the UI can
   * still show the menu/title instead of [object Object].
   */
  private toTranslationDictionary(rows: ResourceDto[], language: string): TranslationObject {
    const cultures = this.culturesForLanguage(language);
    const dictionary: TranslationObject = {};

    for (const row of rows) {
      const culture = row.culture.toLowerCase();
      if (!cultures.has(culture)) {
        continue;
      }

      const key = resourceLabelName(row);
      if (!key) {
        continue;
      }

      this.setNestedValue(dictionary, key, row.value);
    }

    return dictionary;
  }

  /** Application locale `sa` may be stored in the API as `sa` or `ar`. */
  private culturesForLanguage(language: string): Set<string> {
    const code = (language || 'en').toLowerCase();
    if (code === 'sa' || code === 'ar') {
      return new Set(['sa', 'ar']);
    }
    return new Set([code]);
  }

  private setNestedValue(target: TranslationObject, path: string, value: string): void {
    const parts = path.split('.').map((part) => part.trim()).filter(Boolean);
    if (parts.length === 0) {
      return;
    }

    let current: TranslationObject = target;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      const next = current[part];
      if (typeof next === 'string') {
        current[part] = { [TRANSLATION_LEAF]: next };
      } else if (next == null || typeof next !== 'object' || Array.isArray(next)) {
        current[part] = {};
      }
      current = current[part] as TranslationObject;
    }

    const last = parts[parts.length - 1];
    const existing = current[last];
    if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
      (existing as TranslationObject)[TRANSLATION_LEAF] = value;
      return;
    }
    current[last] = value;
  }
}
